import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { blockCustomer } from "../../../core/actionCreators/commonActionCreator";
import { useSelector } from "react-redux";

const LABEL_CLASS = "block text-xs mb-1.5 text-[#5E5D5D] font-medium";
const TEXTAREA_CLASS = "w-full h-24 rounded border text-[#555] border-[#EAEAEA] p-2.5 outline-none text-sm font-light focus:border-blue-400 resize-y";

// Every field accepts a comma separated list, and all of them go up in one request.
const countEntries = (value: string) => value.split(",").map((v) => v.trim()).filter(Boolean).length;

const BlockCustomerForm = () => {
    const [emailList, setEmailList] = useState("")
    const [phoneList, setPhoneList] = useState("")
    const [idList, setIdList] = useState("")

    // Entries the API reported back as not deactivated.
    const [failedEntries, setFailedEntries] = useState<any[]>([])

    const { isLoading } = useSelector((s: any) => s.commonStore)

    const dispatch = useDispatch()

    const totalEntries = countEntries(emailList) + countEntries(phoneList) + countEntries(idList)

    const clearAll = () => {
        setEmailList("")
        setPhoneList("")
        setIdList("")
        setFailedEntries([])
    }

    const blockCustomerHandler = () => {
        let payload: any = {}
        if (emailList) {
            payload.emailList = emailList.split(',').map(item => item.trim())
        }
        if (phoneList) {
            payload.phoneList = phoneList.split(',').map(item => item.trim())
        }
        if (idList) {
            payload.idList = idList.split(',').map(item => item.trim())
        }
        if (Object.keys(payload).length) {
            dispatch(blockCustomer(payload, (_data: any) => {
                setEmailList("")
                setPhoneList("")
                setIdList("")
                if (_data && _data.length) {
                    setFailedEntries(_data)
                    toast.error(`${_data.length} entr${_data.length === 1 ? "y" : "ies"} could not be deactivated.`)
                }
                else {
                    setFailedEntries([])
                    toast.success("Deactivated successfully.")
                }
            }))
        }
        else {
            toast.error("Required fields are missing.")
        }
        return
    }

    return (
        <>
            <div className="p-4 flex items-center">
                <p className="text-xl font-bold text-[#43425D]">BLOCK CUSTOMER</p>
            </div>
            <div className="px-4">
                <div className="w-full bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded mb-5">
                    <p className="bg-[#f6f8f8] p-4 text-base border-b border-b-[#efeff4]">Customer Details</p>
                    <div className="py-4 px-2.5">
                        <p className="px-2 mb-4 text-xs text-[#5E5D5D] font-light">
                            Fill in any combination of the fields below — separate multiple entries with commas. Everything entered is deactivated in a single request.
                        </p>
                        <div className="flex w-full flex-wrap">
                            <div className="w-full md:w-1/3 px-2 mb-4 md:mb-0">
                                <label className={LABEL_CLASS}>
                                    Email Id:
                                    {countEntries(emailList) > 0 && <span className="ml-2 font-light text-[#858585]">{countEntries(emailList)} entered</span>}
                                </label>
                                <textarea
                                    value={emailList}
                                    onChange={(e) => setEmailList(e.target.value)}
                                    placeholder="user1@moglix.com, user2@moglix.com"
                                    className={TEXTAREA_CLASS}
                                />
                            </div>
                            <div className="w-full md:w-1/3 px-2 mb-4 md:mb-0">
                                <label className={LABEL_CLASS}>
                                    Phone Number:
                                    {countEntries(phoneList) > 0 && <span className="ml-2 font-light text-[#858585]">{countEntries(phoneList)} entered</span>}
                                </label>
                                <textarea
                                    value={phoneList}
                                    onChange={(e) => setPhoneList(e.target.value)}
                                    placeholder="9876543210, 9876543211"
                                    className={TEXTAREA_CLASS}
                                />
                            </div>
                            <div className="w-full md:w-1/3 px-2">
                                <label className={LABEL_CLASS}>
                                    Customer Id:
                                    {countEntries(idList) > 0 && <span className="ml-2 font-light text-[#858585]">{countEntries(idList)} entered</span>}
                                </label>
                                <textarea
                                    value={idList}
                                    onChange={(e) => setIdList(e.target.value)}
                                    placeholder="1234567, 1234568"
                                    className={TEXTAREA_CLASS}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {failedEntries.length > 0 && (
                    <div className="w-full bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded mb-5">
                        <p className="bg-[#f6f8f8] p-4 text-base border-b border-b-[#efeff4] text-[#d9232d]">
                            Not deactivated ({failedEntries.length})
                        </p>
                        <div className="py-4 px-2.5">
                            <ul className="px-2">
                                {failedEntries.map((entry: any, index: number) => (
                                    <li key={index} className="text-xs text-[#555] font-light py-1 border-b border-[#EAEAEA] last:border-b-0 break-all">
                                        {typeof entry === "string" ? entry : JSON.stringify(entry)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-4 pb-5">
                    <button
                        onClick={blockCustomerHandler}
                        disabled={isLoading || totalEntries === 0}
                        className="bg-blue-500 text-white px-8 rounded h-9 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Blocking..." : "Block Customer"}
                    </button>
                    <button
                        onClick={clearAll}
                        disabled={totalEntries === 0 && failedEntries.length === 0}
                        className="h-9 px-6 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Clear All
                    </button>
                </div>
            </div>
        </>
    )
}
export default BlockCustomerForm;
